<template>
  <div class="list">
    <div v-for="(h, i) in hotelsList" :key="i">
      <div class="hotel" @click="select(h.id)">
        <img :src="h.image_primary" :class="{ none: h.image_primary == null }" />
        <div class="col1">
          <div class="name">
            {{ h.name }}
          </div>
          <div class="place">
            {{ h.address_city }}
          </div>
          <div class="reviews">
            <div class="rating">
              {{ rating(h.rating_guest * 2) }}
            </div>
            <div class="count">{{ h.rating_guest_count }} reviews</div>
          </div>
        </div>
        <div class="col2">
          <div class="price">
            <div v-if="h.price_display > 0" class="amt">
              <span>{{ priceLabel(h.price_currency_symbol, h.price_currency, h.price_display) }}</span>
            </div>
            <div v-else class="amt">
              <span>Unavailable</span>
            </div>
            <div v-if="h.price_display > 0" class="muted">
              <div>
                {{ priceLabel(h.price_currency_symbol, h.price_currency, h.price_total) }}
                total w/ tax
              </div>
            </div>
          </div>
          <div class="pol" :class="{ within: h.is_policy_within }">
            {{ policyLabel(h.is_policy_within) }}
          </div>
        </div>
      </div>
      <div v-if="0 && (userIsExpert || isDev || userId == 1011214)" class="debug">
        <div v-for="(pd, pdi) in h.price_details" :key="pdi">
          {{ pd }}
        </div>
      </div>
    </div>
    <p v-if="isMax" class="max">Maximum 100 results. Use the search box above to find specific items.</p>
    <div v-if="isLoading" class="hotels-loading">
      <div>Just one moment while we search hundreds of sources...</div>
      <div class="loading">&nbsp;</div>
    </div>
    <div v-if="!isLoading && dataHotels.length == 0" class="hotels-loading">
      <p>Your search returned no results.</p>
      <p>Please consider an <a @click="convert">Assisted Booking</a> where our team will conduct a thorough search of all providers.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject } from "vue";
import useApi from "../utils/apiCalls";
import DateTime from "../utils/datetime";

const emit = defineEmits(["update:isSelfBook", "update:id", "error", "resetFilterSort"]);
const props = defineProps(["filter", "hotels", "legIndex", "organizationId", "sort"]);

const { apiGetRequest } = useApi(emit);
const { utilDateISO8601 } = DateTime();

// States
const dataHotels = ref([]);
const isDev = import.meta.env.MODE.includes("development");
const isLoading = ref(true);
const userIsExpert = inject("userIsExpert");
const userId = inject("userId");

// Computed properties
const isMax = computed(() => dataHotels.value.count >= 100);
const hotelsList = computed(() => {
  let hotels = dataHotels.value;

  if (props.filter.name != "") {
    const name = props.filter.name.toLowerCase();
    hotels = hotels.filter(hotel => hotel.name.toLowerCase().indexOf(name) > -1);
  }

  if (props.filter.star != 0) {
    hotels = hotels.filter(hotel => hotel.rating_star >= props.filter.star);
  }

  if (props.filter.review != 0) {
    hotels = hotels.filter(hotel => hotel.rating_guest * 2 >= props.filter.review);
  }

  if (props.filter.policy == "true") {
    hotels = hotels.filter(hotel => hotel.is_policy_within == true);
  }

  switch (props.sort) {
    case "nearest":
      hotels.sort((a, b) => {
        if (a.rank < b.rank) return -1;
        if (a.rank > b.rank) return 1;

        if (a.price_display > 0 && b.price_display === 0) return -1;
        if (a.price_display === 0 && b.price_display > 0) return 1;

        return 0;
      });
      break;
    case "price-lowest":
      hotels.sort((a, b) => (a.price_display == 0 && b.price_display == 0 ? (a.name < b.name ? -1 : 1) : a.price_display == 0 ? 1 : b.price_display == 0 ? -1 : a.price_display < b.price_display ? -1 : 1));
      break;
    case "price-highest":
      hotels.sort((a, b) => (a.price_display > b.price_display ? -1 : 1));
      break;
    case "guest":
      hotels.sort((a, b) => (a.rating_guest == b.rating_guest ? (a.rating_guest_count > b.rating_guest_count ? -1 : 1) : a.rating_guest > b.rating_guest ? -1 : 1));
      break;
    case "star":
      hotels.sort((a, b) => (a.rating_star == b.rating_star ? (a.rating_guest_count > b.rating_guest_count ? -1 : 1) : a.rating_star > b.rating_star ? -1 : 1));
      break;
    default:
  }

  if (hotels.length >= 100) {
    return hotels.slice(0, 100);
  }

  return hotels;
});

// Methods
const load = () => {
  const placeId = props.hotels.route[props.legIndex].place.id;
  const checkin = utilDateISO8601(props.hotels.route[props.legIndex].checkin);
  const checkout = utilDateISO8601(props.hotels.route[props.legIndex].checkout);
  const adults = props.hotels.route[props.legIndex].rooms.map(room => room.adults).join(",");

  let url = `/properties?&geo=${placeId}&checkin=${checkin}&checkout=${checkout}&adults=${adults}`;

  if (props.organizationId != null && props.organizationId > 0) {
    url += "&organization-id=" + props.organizationId;
  }

  apiGetRequest({
    path: url,
    onSuccess: data => {
      dataHotels.value = data;
      isLoading.value = false;
    },
    onError: error => {
      emit("error", error);
      isLoading.value = false;
    },
  });
};

const convert = () => {
  props.hotels.route.forEach(leg => {
    leg.ideal = {};
  });
  emit("update:isSelfBook", false);
};

const select = id => {
  emit("update:id", id);
};

const priceLabel = (sym, curr, p) => {
  const prefix = sym ? sym : curr;
  return `${prefix}${parseFloat(parseFloat(p).toFixed(2)).toLocaleString("en-US", { minimumFractionDigits: 2, useGrouping: true })}`;
};

const policyLabel = is => (is ? "In Policy" : "Out of Policy");

const rating = rating => (Math.round(rating * 10) / 10).toFixed(1);

// Watchers
watch(
  () => props.legIndex,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      emit("resetFilterSort");
      emit("update:id", null);
      isLoading.value = true;
      dataHotels.value = [];
      load();
    }
  }
);

// Lifecycle hook
onMounted(() => {
  load();
});
</script>

<style lang="less">
#hotels {
  .list {
    margin-bottom: 2rem;

    .hotel {
      background: @light;
      border-radius: 0.25rem;
      box-sizing: border-box;
      cursor: pointer;
      margin-top: 1rem;
      padding: 1rem;
      position: relative;
      white-space: normal;
      width: 100%;

      img {
        display: block;
        height: 7.28125rem;
        margin: 0 auto 1rem;
        object-fit: cover;
        width: 10.9375rem;

        &.none {
          opacity: 0;
        }
      }

      .col1,
      .col2 {
        text-align: center;
      }

      .name {
        font-size: 1.125rem;
      }

      .place {
        color: @mid;
        margin-bottom: 1rem;
      }

      .reviews {
        color: @mid;
      }

      .rating {
        background: @tertiary;
        border-radius: 0.25rem;
        display: inline-block;
        color: @dark;
        padding: 0.125rem 0.25rem;
      }

      .count {
        flex: 1;
        padding: 0.125rem 0;
      }

      .price {
        margin: 1rem 0;

        .amt {
          font-size: 1.125rem;
          flex: 1;
        }
      }

      .pol {
        color: @secondary;

        &.within {
          color: @green;
        }
      }
    }
  }

  .debug {
    background: @lighter;
    padding: 1rem;
  }

  .hotels-loading {
    background: @light;
    padding: 2rem;
    text-align: center;

    p {
      margin: 0 auto 2rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .loading {
      margin-top: 2rem;

      &:after {
        background: transparent;
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #hotels {
    .list {
      .hotel {
        display: flex;

        img {
          margin: 0;
        }

        .col1 {
          flex: 1;
          padding: 0 2rem;
          text-align: left;
        }

        .reviews {
          display: flex;
        }

        .rating {
          margin-right: 0.5rem;
        }

        .col2 {
          text-align: right;
        }

        .price {
          margin: 0 0 1rem;

          .amt {
            font-size: 1.125rem;
          }
        }

        .pol {
          text-align: right;
        }
      }
    }
  }
}
</style>
